import { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Plus, Trash2, Star, AlertTriangle, CheckCircle, Clock, Upload } from 'lucide-react';
import { DigitalCertificate } from '@/types';
import { toast } from 'sonner';
import { MigrarCifrado } from '@/components/MigrarCifrado';

export default function Certificados() {
  const { fetchWithAuth } = useUser();
  const [certificados, setCertificados] = useState<DigitalCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Formulario de subida
  const [formData, setFormData] = useState({
    nombre: '',
    certificatePem: '',
    privateKeyPem: '',
    p12File: null as File | null,
    p12Password: '',
  });

  // Cargar certificados
  useEffect(() => {
    loadCertificados();
  }, []);

  const loadCertificados = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/certificados');
      const data = await response.json();
      setCertificados(data);
    } catch (error) {
      console.error('Error al cargar certificados:', error);
      toast.error('Error al cargar certificados');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCertificate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar según el tipo de archivo
    if (!formData.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }

    if (formData.p12File) {
      // Subida de archivo .p12
      if (!formData.p12Password) {
        toast.error('Se requiere la contraseña del certificado .p12');
        return;
      }

      try {
        setUploading(true);
        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64Data = (event.target?.result as string).split(',')[1];

          const response = await fetchWithAuth('/certificados/upload-p12', {
            method: 'POST',
            body: JSON.stringify({
              nombre: formData.nombre,
              p12Data: base64Data,
              password: formData.p12Password,
            }),
          });

          const data = await response.json();

          if (response.ok) {
            toast.success(`Certificado registrado correctamente. Expira en ${data.expiresIn}`);
            setUploadDialogOpen(false);
            setFormData({ nombre: '', certificatePem: '', privateKeyPem: '', p12File: null, p12Password: '' });
            loadCertificados();
          } else {
            toast.error(data.error || 'Error al registrar certificado');
          }
          setUploading(false);
        };
        reader.readAsDataURL(formData.p12File);
      } catch (error) {
        console.error('Error al subir certificado:', error);
        toast.error('Error al subir certificado');
        setUploading(false);
      }
    } else {
      // Subida manual de PEM
      if (!formData.certificatePem || !formData.privateKeyPem) {
        toast.error('Debes proporcionar el certificado y la clave privada en formato PEM');
        return;
      }

      try {
        setUploading(true);
        const response = await fetchWithAuth('/certificados', {
          method: 'POST',
          body: JSON.stringify({
            nombre: formData.nombre,
            certificatePem: formData.certificatePem,
            privateKeyPem: formData.privateKeyPem,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          toast.success(`Certificado registrado correctamente. Expira en ${data.expiresIn}`);
          setUploadDialogOpen(false);
          setFormData({ nombre: '', certificatePem: '', privateKeyPem: '', p12File: null, p12Password: '' });
          loadCertificados();
        } else {
          toast.error(data.error || 'Error al registrar certificado');
        }
      } catch (error) {
        console.error('Error al subir certificado:', error);
        toast.error('Error al subir certificado');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const response = await fetchWithAuth(`/certificados/${id}/set-default`, {
        method: 'PUT',
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        loadCertificados();
      } else {
        toast.error(data.error || 'Error al establecer certificado predeterminado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar certificado');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este certificado?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/certificados/${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        loadCertificados();
      } else {
        toast.error(data.error || 'Error al eliminar certificado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar certificado');
    }
  };

  const getCertificateStatus = (validUntil: string) => {
    const now = new Date();
    const expiration = new Date(validUntil);
    const daysUntilExpiration = Math.ceil((expiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiration < 0) {
      return { status: 'expired', label: 'Expirado', color: 'destructive', icon: AlertTriangle };
    } else if (daysUntilExpiration <= 30) {
      return { status: 'expiring', label: `Expira en ${daysUntilExpiration} días`, color: 'warning', icon: Clock };
    } else {
      return { status: 'valid', label: `Válido (${daysUntilExpiration} días)`, color: 'success', icon: CheckCircle };
    }
  };

  const handleFileUpload = (field: 'certificatePem' | 'privateKeyPem') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setFormData((prev) => ({ ...prev, [field]: content }));
      };
      reader.readAsText(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando certificados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Certificados Digitales
          </h1>
          <p className="text-muted-foreground mt-2">
            Gestiona tus certificados para firmar facturas electrónicamente
          </p>
        </div>

        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Añadir Certificado
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Subir Certificado Digital</DialogTitle>
              <DialogDescription>
                Sube tu certificado digital en formato PKCS#12 (.p12/.pfx) o PEM para firmar facturas electrónicamente.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUploadCertificate} className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre descriptivo</Label>
                <Input
                  id="nombre"
                  placeholder="Ej: Certificado FNMT 2025"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
              </div>

              {/* Opción 1: Archivo P12/PFX */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-sm">Opción 1: Certificado PKCS#12 (Recomendado para FNMT)</h3>
                <div>
                  <Label htmlFor="p12File">Archivo .p12 o .pfx</Label>
                  <Input
                    id="p12File"
                    type="file"
                    accept=".p12,.pfx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setFormData({ ...formData, p12File: file || null, certificatePem: '', privateKeyPem: '' });
                    }}
                  />
                </div>
                {formData.p12File && (
                  <div>
                    <Label htmlFor="p12Password">Contraseña del certificado</Label>
                    <Input
                      id="p12Password"
                      type="password"
                      placeholder="Contraseña"
                      value={formData.p12Password}
                      onChange={(e) => setFormData({ ...formData, p12Password: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Opción 2: PEM manual */}
              {!formData.p12File && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-sm">Opción 2: Certificado y clave en formato PEM</h3>
                  <div>
                    <Label htmlFor="certificatePem">Certificado (PEM)</Label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        type="file"
                        accept=".pem,.crt,.cer"
                        onChange={handleFileUpload('certificatePem')}
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" size="sm">
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      id="certificatePem"
                      placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                      value={formData.certificatePem}
                      onChange={(e) => setFormData({ ...formData, certificatePem: e.target.value })}
                      rows={6}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div>
                    <Label htmlFor="privateKeyPem">Clave Privada (PEM)</Label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        type="file"
                        accept=".pem,.key"
                        onChange={handleFileUpload('privateKeyPem')}
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" size="sm">
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      id="privateKeyPem"
                      placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                      value={formData.privateKeyPem}
                      onChange={(e) => setFormData({ ...formData, privateKeyPem: e.target.value })}
                      rows={6}
                      className="font-mono text-xs"
                    />
                  </div>
                </div>
              )}

              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <strong>Seguridad:</strong> Tu certificado y clave privada se cifrarán con AES-256-GCM antes de almacenarse.
                  Nunca se expondrán al navegador durante la firma.
                </AlertDescription>
              </Alert>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Subiendo...' : 'Registrar Certificado'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Temporal (SEC-04): migración del cifrado antiguo */}
      <MigrarCifrado />

      {/* Información */}
      {certificados.length === 0 && (
        <Alert className="mb-6">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <strong>¿Cómo obtener un certificado digital?</strong>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li><strong>FNMT:</strong> www.fnmt.es (Fábrica Nacional de Moneda y Timbre)</li>
              <li><strong>Camerfirma:</strong> www.camerfirma.com</li>
              <li><strong>ANF:</strong> www.anf.es (Agencia de Notificación y Firma)</li>
            </ul>
            <p className="mt-2 text-sm">
              Para pruebas, puedes generar un certificado autofirmado ejecutando:
              <code className="bg-muted px-2 py-1 rounded ml-2">node scripts/generate-test-certificate.cjs</code>
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Lista de Certificados */}
      {certificados.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Mis Certificados ({certificados.length})</CardTitle>
            <CardDescription>
              El certificado marcado como predeterminado se usará automáticamente para firmar facturas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Predeterminado</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Emisor</TableHead>
                  <TableHead>Sujeto</TableHead>
                  <TableHead>Válido Hasta</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[80px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificados.map((cert) => {
                  const status = getCertificateStatus(cert.validUntil);
                  const StatusIcon = status.icon;

                  return (
                    <TableRow key={cert.id}>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          <input
                            type="radio"
                            name="defaultCertificate"
                            checked={cert.isDefault}
                            onChange={() => handleSetDefault(cert.id)}
                            className="w-4 h-4 cursor-pointer accent-primary"
                            title="Seleccionar como predeterminado"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{cert.nombre}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                          {cert.issuer}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                          {cert.subject}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {new Date(cert.validUntil).toLocaleDateString('es-ES')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.color as any} className="flex items-center gap-1 w-fit">
                          <StatusIcon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(cert.id)}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No tienes certificados registrados</h3>
            <p className="text-muted-foreground text-center mb-6">
              Sube tu certificado digital para comenzar a firmar facturas electrónicamente
            </p>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Añadir Primer Certificado
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Información adicional */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">¿Para qué sirve la firma electrónica?</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              • <strong>Obligatorio</strong> para facturas a Administraciones Públicas (FACe)
            </p>
            <p>
              • Garantiza la <strong>autenticidad</strong> e <strong>integridad</strong> de la factura
            </p>
            <p>
              • Cumple con el formato <strong>Facturae 3.2.2</strong> y firma <strong>XAdES-BES</strong>
            </p>
            <p>
              • Válido legalmente según la <strong>Ley 25/2013</strong>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Seguridad de tus certificados</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              • Cifrado <strong>AES-256-GCM</strong> antes de almacenar
            </p>
            <p>
              • Claves privadas <strong>nunca</strong> se envían al navegador
            </p>
            <p>
              • Firma realizada de forma <strong>segura</strong> en el servidor
            </p>
            <p>
              • Solo tú puedes acceder a tus certificados
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
